import React, { FC, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon, Tooltip } from "@shared/components";
import { actions as sharedActions } from "@shared/store";
import { BadgeCardViewModel } from "@containers/Recognition/components";

// CATEGORY_LABELS is a real value import (not type-only), so — like ValueLadderCards' reuse of
// CoreValueCard (phase-35 plan) — it must be a relative import: jest has no moduleNameMapper entry
// for cross-container aliases, only type-only imports "work" there because babel elides them first.
// Imported from its own small leaf file on the shared components/Badges/ shelf (not from a component
// that happens to render it) to keep this container's test from having to pull in
// buildBadgeCards/BadgeCard just to resolve one shared label map.
import { CATEGORY_LABELS } from "../../components/Badges/categoryLabels";
// Same reasoning, same direct-path convention — never through a barrel
// (overview-rebuild-pr3-plan.md "One helper owns the points string").
import { pointsToNextCaption } from "../../components/Badges/pointsToNextCaption";
// Same shelf, same direct-path convention — the bundled-art resolver `BadgeCard` also uses.
import { resolveBadgeIcon } from "../../components/Badges/badgeArt";
// Same shelf, same direct-path convention — the locked-badge silhouette `BadgeCard` also uses.
import MysteryBadgeArt from "../../components/Badges/MysteryBadgeArt/MysteryBadgeArt";
// Real (non-type-only) selector import, reached straight into `store/selectors` rather than the
// `store` barrel for the identical reason as the two imports above.
import { getCoreValues } from "../../store/selectors";

import "./index.scss";

interface BadgeOverlayDetailsContainerProps {
  badge: BadgeCardViewModel;
  onClose: () => void;
}

/**
 * The overlay shown when a badge card is clicked (name/description/category/rarity, plus the shared
 * "N Sparks to go" caption when the badge is its ladder's own next tier). Mirrors
 * `RecognitionOverlayDetailsContainer`'s own convention: the
 * close icon is the real `Icon` component (`type="clear"`), not a hand-rolled glyph. Unlike that
 * container, this one is also handed the `onClose` `OverlayWrapper` injects into every overlay
 * component's props — closing here both dispatches `hideOverlay()` itself AND fires the injected
 * `onClose`, so a caller-supplied `showOverlay({ props: { onClose } })` callback (if any) still runs.
 *
 * A LOCKED badge's real name and picture are hidden here too (pavel's decision, mirroring `BadgeCard`):
 * the title reads the plain, literal "Locked badge" (a single occurrence, unlike the tile grid, so it
 * needs no shared constant) and the art is `MysteryBadgeArt`'s silhouette. Category, rarity, the
 * criteria `description` and the points-to-go caption are unaffected — only the name and the art are
 * hidden.
 */
const BadgeOverlayDetailsContainer: FC<BadgeOverlayDetailsContainerProps> = (props) => {
  const { badge, onClose } = props;
  const { name, description, category, rarity, iconUrl, earned, pointsToNext, code } = badge;
  const dispatch = useDispatch();
  const coreValues = useSelector(getCoreValues());
  const pointsCaption = pointsToNextCaption(pointsToNext);
  // Same resolution rule as `BadgeCard` (its own doc comment has the full reasoning): a per-value,
  // flame, tenure or achievement badge's `iconUrl` is null by design, so this reaches for the bundled
  // asset only then; a real `iconUrl` wins outright, and a code with no bundled art resolves to null.
  // Every screen that opens this overlay (My Sparks, the Badges screen, a colleague's profile) already
  // dispatched `getCoreValues.request()` to render its own tiles, so this container needs no fetch.
  const resolvedIconUrl = resolveBadgeIcon(iconUrl, code, coreValues);
  const displayName = earned ? name : "Locked badge";

  const closeBadgeDetailsOverlay = useCallback(() => {
    dispatch(sharedActions.hideOverlay());
    onClose();
  }, [dispatch, onClose]);

  return (
    <div className="badge-details-container-wrapper">
      <div className="close-icon">
        <Tooltip text="Close">
          <button type="button" className="close-icon-button" aria-label="Close" onClick={closeBadgeDetailsOverlay}>
            <Icon type="clear" />
          </button>
        </Tooltip>
      </div>
      {earned ? (
        <div
          className="badge-details-icon"
          style={resolvedIconUrl ? { backgroundImage: `url("${resolvedIconUrl}")` } : undefined}
        />
      ) : (
        <MysteryBadgeArt iconUrl={resolvedIconUrl} className="badge-details-icon" />
      )}
      <div className="badge-details-title">{displayName}</div>
      <div className="badge-details-meta">
        <span className="badge-details-category">{CATEGORY_LABELS[category]}</span>
        <span className="badge-details-rarity">{rarity}</span>
      </div>
      {description ? <div className="badge-details-description">{description}</div> : null}
      {/* The overlay's own progress bar goes (overview-rebuild-pr3-plan.md "Consequence 1 — the
          overlay's own progress bar"): it renders the SAME distance-to-go text the card does, shared
          via pointsToNextCaption, rather than an independent bar that could disagree with the card
          about the same badge. */}
      {pointsCaption !== null ? <div className="badge-details-points-caption">{pointsCaption}</div> : null}
    </div>
  );
};

export default BadgeOverlayDetailsContainer;
